import { HttpError } from '@littlebird/auth-client';
import { captureException } from '@sentry/react';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import {
  CALENDAR_DATA_SECTION_TITLE,
  uploadLocalCalendarEvents,
  useSnapshotDumps,
} from '@/features/feedback';
import {
  clearFeedbackDraftEdit,
  readFeedbackDraftEdit,
  saveFeedbackDraftEdit,
} from '@/features/feedback-report/feedback-draft-edits';
import {
  useFeedbackDraftQuery,
  useSendFeedbackDraftMutation,
} from '@/features/feedback-report/feedback-report.query';
import { VoteDataDirection } from '@/query/vote';
import { userStore } from '@/stores/user.store';
import { submitSurvey, trackEvent } from '@/utils/analytics';
import {
  formatInfoSection,
  formatUrlSection,
} from '@/utils/feedback-report-format';

import type { ProductFeedbackType } from '@littlebird/ui-utils';

export type FeedbackReportSendState = 'idle' | 'sending' | 'sent';

function userErrorMessage(error: unknown): string | undefined {
  if (!(error instanceof HttpError)) return undefined;
  const message = error.body?.detail?.user_error_message;
  return typeof message === 'string' ? message : undefined;
}

export interface UseSendFeedbackReportCardArgs {
  draftId: string;
}

export function useSendFeedbackReportCard({
  draftId,
}: UseSendFeedbackReportCardArgs) {
  const [includeChatHistory, setIncludeChatHistory] = useState(true);
  const [includeCalendarEvents, setIncludeCalendarEvents] = useState(false);
  const [isUploadingCalendarEvents, setIsUploadingCalendarEvents] =
    useState(false);
  const [reportText, setReportText] = useState('');
  const [feedbackType, setFeedbackType] =
    useState<ProductFeedbackType>('other');
  const [seededDraftId, setSeededDraftId] = useState<string | null>(null);

  const draftQuery = useFeedbackDraftQuery(draftId);
  const sendDraft = useSendFeedbackDraftMutation();

  const {
    apps: snapshotDumpApps,
    selectedApps: selectedDumpApps,
    toggleApp: toggleDumpApp,
    uploadSelectedDumps,
    isPreparing: isPreparingDumps,
    shouldShow: shouldShowDumpSelector,
  } = useSnapshotDumps({ feedbackType });

  const draft = draftQuery.data;
  const contentType = draft?.content.type;
  // Only message feedback carries a sentiment; product feedback has none.
  const voteSentiment =
    draft?.content.type === 'message_feedback'
      ? draft.content.sentiment
      : undefined;
  const isCalendarRelated =
    draft?.content.type === 'product_feedback' &&
    draft.content.calendar_related;

  const loadErrorMessage = draftQuery.isError
    ? (userErrorMessage(draftQuery.error) ??
      (draftQuery.error instanceof HttpError
        ? draftQuery.error.message
        : undefined) ??
      'Couldn’t load this feedback draft')
    : null;

  const sendState: FeedbackReportSendState =
    draft?.status === 'sent' || sendDraft.isSuccess
      ? 'sent'
      : sendDraft.isPending || isPreparingDumps || isUploadingCalendarEvents
        ? 'sending'
        : 'idle';

  // Seed the editable fields from the draft once, when the query first resolves.
  if (draft && seededDraftId !== draft.id) {
    setSeededDraftId(draft.id);
    const savedEdit =
      draft.status === 'sent' ? undefined : readFeedbackDraftEdit(draft.id);
    setReportText(
      savedEdit?.text ??
        (draft.content.type === 'message_feedback'
          ? draft.content.reasoning
          : draft.content.text)
    );
    if (draft.content.type === 'product_feedback') {
      setFeedbackType(savedEdit?.feedbackType ?? draft.content.feedback_type);
    }
  }

  const updateReportText = useCallback(
    (text: string) => {
      setReportText(text);
      saveFeedbackDraftEdit(draftId, { text });
    },
    [draftId]
  );

  const updateFeedbackType = useCallback(
    (type: ProductFeedbackType) => {
      setFeedbackType(type);
      saveFeedbackDraftEdit(draftId, { feedbackType: type });
    },
    [draftId]
  );

  const handleSend = useCallback(async () => {
    if (sendState !== 'idle' || !draft) return;
    const trimmedReport = reportText.trim();
    const { content } = draft;

    const appendSnapshotDumps = async (text: string): Promise<string> => {
      if (selectedDumpApps.size === 0) return text;
      try {
        const dumpUrls = await uploadSelectedDumps();
        if (dumpUrls.length > 0) {
          return text + formatUrlSection('Snapshot Dumps', dumpUrls);
        }
      } catch (dumpError) {
        console.error('Failed to upload snapshot dumps:', dumpError);
      }
      toast.error(
        'Failed to prepare snapshot dumps, but your feedback will still be sent'
      );
      return text;
    };

    const appendCalendarEvents = async (text: string): Promise<string> => {
      if (!isCalendarRelated || !includeCalendarEvents) return text;
      setIsUploadingCalendarEvents(true);
      try {
        const calendarS3Key = await uploadLocalCalendarEvents();
        return (
          text + formatUrlSection(CALENDAR_DATA_SECTION_TITLE, [calendarS3Key])
        );
      } catch (calendarError) {
        console.error('Failed to upload calendar events:', calendarError);
        toast.error(
          'Failed to upload calendar events, but your feedback will still be sent'
        );
        return text;
      } finally {
        setIsUploadingCalendarEvents(false);
      }
    };

    try {
      switch (content.type) {
        case 'product_feedback': {
          const text = await appendCalendarEvents(
            await appendSnapshotDumps(trimmedReport + formatInfoSection())
          );
          await sendDraft.mutateAsync({
            draftId,
            type: 'product_feedback',
            text,
            feedbackType,
          });
          clearFeedbackDraftEdit(draftId);
          submitSurvey('feedback & support', [
            feedbackType,
            userStore.user?.email ?? 'anonymous',
            text,
          ]).catch(captureException);
          break;
        }
        case 'message_feedback': {
          await sendDraft.mutateAsync({
            draftId,
            type: 'message_feedback',
            reasoning: trimmedReport,
            includeChatHistory,
          });
          clearFeedbackDraftEdit(draftId);
          const direction =
            content.sentiment === 'positive'
              ? VoteDataDirection.UP
              : VoteDataDirection.DOWN;
          trackEvent('MESSAGE_RATED', {
            chat_id: draft.chat_id,
            message_id: content.message_id,
            direction,
            source: 'agent_feedback',
          });
          submitSurvey('chat message feedback', [
            draft.chat_id,
            content.message_id,
            direction === VoteDataDirection.UP ? 'Up' : 'Down',
            trimmedReport,
          ]).catch(captureException);
          break;
        }
      }
    } catch (e) {
      toast.error(userErrorMessage(e) ?? 'Failed to send feedback');
    }
  }, [
    sendState,
    draft,
    sendDraft,
    draftId,
    reportText,
    includeChatHistory,
    feedbackType,
    selectedDumpApps,
    uploadSelectedDumps,
    isCalendarRelated,
    includeCalendarEvents,
  ]);

  return {
    contentType,
    voteSentiment,
    sendState,
    draftQuery,
    loadErrorMessage,
    refetch: draftQuery.refetch,
    reportText,
    setReportText: updateReportText,
    includeChatHistory,
    setIncludeChatHistory,
    isCalendarRelated,
    includeCalendarEvents,
    setIncludeCalendarEvents,
    feedbackType,
    setFeedbackType: updateFeedbackType,
    handleSend,
    snapshotDumpApps,
    selectedDumpApps,
    toggleDumpApp,
    isPreparingDumps,
    shouldShowDumpSelector,
  };
}
